/**
 * FacultyDashboardPage.js
 * Q.Bank review queue for faculty — list pending reviews, approve or reject.
 */
import React, { useEffect, useState, useCallback } from 'react';
import ExamPageShell from '../components/layout/ExamPageShell';
import EmptyState from '../components/common/EmptyState';
import { listPendingApprovals, approveQBank, rejectQBank } from '../api/examApi';

function ReviewCard({ review, onApprove, onReject, busy }) {
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(false);

  const statusColor = {
    pending:  'var(--exam-warning)',
    approved: 'var(--exam-success)',
    rejected: 'var(--exam-error)',
  }[review.status] || 'var(--exam-text-muted)';

  return (
    <div className="exam-card" style={{ padding: '16px 20px', borderLeft: `3px solid ${statusColor}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--exam-text)' }}>
              {review.project_name || `Project #${review.project_id}`}
            </span>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
              background: review.status === 'pending' ? 'var(--exam-warning-light)' : review.status === 'approved' ? 'var(--exam-success-light)' : 'var(--exam-error-light)',
              color: statusColor,
              textTransform: 'capitalize',
            }}>
              {review.status}
            </span>
          </div>
          {review.notes && (
            <p style={{ fontSize: 12.5, color: 'var(--exam-text-secondary)', margin: '0 0 6px', lineHeight: 1.5 }}>
              <strong>Submitter note:</strong> {review.notes}
            </p>
          )}
          <div style={{ fontSize: 11, color: 'var(--exam-text-muted)' }}>
            Submitted: {review.submitted_at ? new Date(review.submitted_at).toLocaleString() : '—'}
            {review.submitted_by && ` · by ${review.submitted_by}`}
          </div>
        </div>

        {review.status === 'pending' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <button
              className="exam-btn exam-btn-sm"
              style={{ background: 'var(--exam-success-light)', color: 'var(--exam-success)', minWidth: 80 }}
              onClick={() => onApprove(review.id, notes)}
              disabled={busy}
            >
              ✓ Approve
            </button>
            <button
              className="exam-btn exam-btn-ghost exam-btn-sm"
              style={{ color: 'var(--exam-error)', minWidth: 80 }}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? '▲ Cancel' : '✕ Reject'}
            </button>
          </div>
        )}
      </div>

      {expanded && review.status === 'pending' && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--exam-border)' }}>
          <textarea
            className="exam-input"
            rows={2}
            placeholder="Reason for rejection (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ width: '100%', resize: 'vertical', marginBottom: 8 }}
          />
          <button
            className="exam-btn exam-btn-outline exam-btn-sm"
            style={{ color: 'var(--exam-error)', borderColor: 'var(--exam-error)' }}
            onClick={() => { onReject(review.id, notes); setExpanded(false); }}
            disabled={busy}
          >
            Confirm Rejection
          </button>
        </div>
      )}
    </div>
  );
}

export default function FacultyDashboardPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(() => {
    setLoading(true);
    listPendingApprovals()
      .then((data) => setReviews(Array.isArray(data) ? data : (data.reviews || [])))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, notes) => {
    setBusy(true);
    try { await approveQBank(id, notes); load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Approval failed.'); }
    finally { setBusy(false); }
  };

  const handleReject = async (id, notes) => {
    setBusy(true);
    try { await rejectQBank(id, notes); load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Rejection failed.'); }
    finally { setBusy(false); }
  };

  const filtered = reviews.filter((r) => filter === 'all' || r.status === filter);

  return (
    <ExamPageShell breadcrumbs={[{ label: 'Faculty View' }]}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--exam-text)' }}>Faculty Dashboard</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--exam-text-muted)' }}>
            Review and approve Q.Bank submissions from your institution.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Pending', count: reviews.filter((r) => r.status === 'pending').length, color: 'var(--exam-warning)' },
            { label: 'Approved', count: reviews.filter((r) => r.status === 'approved').length, color: 'var(--exam-success)' },
            { label: 'Rejected', count: reviews.filter((r) => r.status === 'rejected').length, color: 'var(--exam-error)' },
          ].map((s) => (
            <div key={s.label} className="exam-card" style={{ padding: '12px 20px', minWidth: 120, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 11, color: 'var(--exam-text-muted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
          <button className="exam-btn exam-btn-ghost exam-btn-sm" style={{ marginLeft: 'auto', alignSelf: 'center' }} onClick={load}>
            ↻ Refresh
          </button>
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[{ key: 'pending', label: 'Pending' }, { key: 'approved', label: 'Approved' }, { key: 'rejected', label: 'Rejected' }, { key: 'all', label: 'All' }].map(({ key, label }) => (
            <button key={key}
              className={`exam-btn exam-btn-sm ${filter === key ? 'exam-btn-primary' : 'exam-btn-ghost'}`}
              onClick={() => setFilter(key)}
            >{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="exam-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="📋" title={filter === 'pending' ? 'No pending reviews' : 'No reviews'} description="" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map((r) => (
              <ReviewCard key={r.id} review={r} onApprove={handleApprove} onReject={handleReject} busy={busy} />
            ))}
          </div>
        )}
      </div>
    </ExamPageShell>
  );
}
